import React from 'react';
import { Tabs } from '../../components/ui';
import { useTabParam } from '../../hooks/useTabParam.js';
import { Settings, GitBranch, Zap, type LucideIcon } from 'lucide-react';
import { WorkflowSettingsForm } from './WorkflowSettingsPanel.js';
import type { WorkflowSettingsFormProps } from './WorkflowSettingsPanel.js';
import { ExecutionFlowContent } from './ExecutionFlowPanel.js';
import { StepConfigurationPanel } from './StepConfigurationPanel.js';
import type { WorkflowBuilderStep, ConfigTab } from './WorkflowBuilderTypes.js';
import type { FieldChange } from '../../hooks/useFieldChangeTracker.js';
import type {
  SaveDebugStepOutputData,
  SaveDebugStepOutputResponse,
  AgentTemplateSummary,
} from '../../generated/api/types.gen.js';
import type { DiagramStep } from '../../components/workflow/WorkflowFlowDiagram.js';
import type { MultiStepAgentStream } from '../../hooks/useMultiStepAgentStream.js';
import type { PreviewPromptRole } from './promptPreview.types.js';

type TabId = 'settings' | 'flow' | 'steps';
type SaveDebugStepOutputBody = NonNullable<SaveDebugStepOutputData['body']>;

interface WorkflowBuilderTabsProps extends WorkflowSettingsFormProps {
  // Flow
  diagramSteps: DiagramStep[];
  onDirectionToggle: () => void;
  onPositionsChange: (positions: Record<string, { x: number; y: number }>) => void;
  // Steps
  steps: WorkflowBuilderStep[];
  selectedStep: string | null;
  sel: WorkflowBuilderStep | undefined;
  stepLabels: Map<string, string>;
  stepResults: Record<string, unknown>;
  onSelectStep: (stepId: string | null) => void;
  onAddStep: () => void;
  onRemoveStep: (stepId: string) => void;
  activeTab: ConfigTab;
  onTabChange: (tab: ConfigTab) => void;
  roles: AgentTemplateSummary[];
  onUpdateStep: (stepId: string, updates: Partial<WorkflowBuilderStep>) => void;
  promptPreview: {
    systemPrompt: string;
    userPrompt: string;
    estimatedTokens: number;
    role: PreviewPromptRole;
  } | null;
  promptLoading: boolean;
  instructionMode: 'freetext' | 'template';
  onInstructionModeChange: (mode: 'freetext' | 'template') => void;
  onOpenPromptBuilder: () => void;
  templateId: string | undefined;
  isNew: boolean;
  multiStream: MultiStepAgentStream;
  onDebugResult: (stepId: string, result: unknown) => void;
  onFocusToggle: (focused: boolean) => void;
  agentApi: { saveDebugStepOutput: (params: SaveDebugStepOutputBody) => Promise<SaveDebugStepOutputResponse> };
  changes?: Map<string, FieldChange>;
  debugStatuses?: Record<string, 'has_output' | 'drifted'>;
  stepWarnings?: Record<string, Array<{ message: string; severity: 'error' | 'warning' }>>;
}

const tabDef: { id: TabId; icon: LucideIcon; label: string }[] = [
  { id: 'settings', icon: Settings, label: 'Settings' },
  { id: 'flow', icon: GitBranch, label: 'Flow' },
  { id: 'steps', icon: Zap, label: 'Steps' },
];

const TAB_IDS = tabDef.map((t) => t.id);

export const WorkflowBuilderTabs: React.FC<WorkflowBuilderTabsProps> = (props) => {
  // A separate param: the builder is also embedded in pages that own `?tab=`.
  const [activeTopTab, setActiveTopTab] = useTabParam(TAB_IDS, 'steps', 'builderTab');

  const handleFlowStepClick = (stepId: string) => {
    props.onSelectStep(stepId);
    props.onTabChange('config');
    setActiveTopTab('steps');
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Tab bar */}
      <Tabs
        aria-label="Workflow sections"
        tabs={tabDef
          .filter(({ id }) => !(id === 'flow' && props.steps.length === 0))
          .map(({ id, icon, label }) => ({
            id,
            icon,
            label,
            count: id === 'steps' ? props.steps.length : undefined,
          }))}
        activeTab={activeTopTab}
        onChange={setActiveTopTab}
        className="shrink-0 rounded-t-lg border bg-surface px-2"
      />

      {/* Tab content — full remaining height */}
      {activeTopTab === 'settings' && (
        <div className="flex-1 min-h-0 overflow-auto bg-surface rounded-b-lg border-x border-b pt-4">
          <WorkflowSettingsForm form={props.form} updateField={props.updateField} changes={props.changes} />
        </div>
      )}

      {activeTopTab === 'flow' && props.steps.length > 0 && (
        <div className="flex-1 min-h-0 flex flex-col bg-surface rounded-b-lg border-x border-b overflow-hidden">
          <ExecutionFlowContent
            diagramSteps={props.diagramSteps}
            selectedStepId={props.selectedStep}
            onStepClick={handleFlowStepClick}
            onOutputClick={(stepId) => {
              props.onSelectStep(stepId);
              props.onTabChange('output');
              setActiveTopTab('steps');
            }}
            flowDirection={props.form.flowDirection}
            onDirectionToggle={props.onDirectionToggle}
            onPositionsChange={props.onPositionsChange}
          />
        </div>
      )}

      {activeTopTab === 'steps' && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <StepConfigurationPanel
            stepsCollapsed={false}
            stepsExpanded={false}
            focusMode={false}
            onExpandToggle={() => {}}
            onToggle={() => {}}
            steps={props.steps}
            selectedStep={props.selectedStep}
            sel={props.sel}
            stepLabels={props.stepLabels}
            stepResults={props.stepResults}
            onSelectStep={props.onSelectStep}
            onAddStep={props.onAddStep}
            onRemoveStep={props.onRemoveStep}
            activeTab={props.activeTab}
            onTabChange={props.onTabChange}
            roles={props.roles}
            onUpdateStep={props.onUpdateStep}
            promptPreview={props.promptPreview}
            promptLoading={props.promptLoading}
            instructionMode={props.instructionMode}
            onInstructionModeChange={props.onInstructionModeChange}
            onOpenPromptBuilder={props.onOpenPromptBuilder}
            templateId={props.templateId}
            isNew={props.isNew}
            multiStream={props.multiStream}
            onDebugResult={props.onDebugResult}
            onFocusToggle={props.onFocusToggle}
            agentApi={props.agentApi}
            changes={props.changes}
            debugStatuses={props.debugStatuses}
            stepWarnings={props.stepWarnings}
          />
        </div>
      )}
    </div>
  );
};
